<script lang="ts" setup>
import config from '~~/config.json'
import localforage from 'localforage'
const defaultResolution: VideoResolution = '1080P'
const resolutionList: VideoResolution[] = ['360P', '540P', '720P', '1080P']

const lightoff = useState<boolean>('player_light_off')
const fullscreen = useState<boolean>('player_fullscreen')
const widescreen = useState<boolean>('player_widescreen')

function getResolution() {
  // return (
  //   (localStorage.getItem('user_set_resolution') as VideoResolution) ||
  //   defaultResolution
  // )
  return defaultResolution
}

function setResolution(resolution: VideoResolution) {
  localStorage.setItem('user_set_resolution', resolution)
}

let userSetResolution: VideoResolution = getResolution()
if (!resolutionList.includes(userSetResolution)) {
  userSetResolution = defaultResolution
  setResolution(userSetResolution)
}

const props = defineProps<{
  videoId: number | string
  videoList: Video.VideoItem[]
  paragraph: number
  danmakuEleId: string
}>()

const emit = defineEmits(['update:paragraph'])
const screen = useScreen()
const loaded = ref(false)
const list: VideoList = []
const currentVideo = computed({
  get() {
    return props.paragraph - 1
  },
  set(index: number) {
    emit('update:paragraph', index + 1)
  }
})

const playerConfig = usePlayerConfig().getConfig()
const paddingBottom = playerConfig.blackBorder
  ? 'calc(56.25% + 90px + 40px)'
  : 'calc(56.25% + 40px)'

function getList() {
  props.videoList.map(async (item, index) => {
    if (item.video_url.length === 1) {
      list.push({
        pic: 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=',
        title: item.title,
        url: item.video_url[0]?.url || '',
        type: item.video_url[0]?.format || 'mp4',
        danId: props.videoId + '&part=' + (index + 1)
      })
    } else {
      const defaultResolution =
        parseInt(userSetResolution) -
        Math.min(
          ...item.video_url.map((item) =>
            Math.abs(parseInt(item.name) - parseInt(userSetResolution))
          )
        ) +
        'P'
      const defaultIndex = item.video_url.findIndex(
        (item) => item.name === defaultResolution
      )
      list.push({
        pic: 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=',
        title: item.title,
        url: item.video_url[defaultIndex]?.url || '',
        type: item.video_url[defaultIndex]?.format || 'mp4',
        danId: props.videoId + '&part=' + (index + 1),
        resolution: item.video_url.map((item, i) => {
          return {
            url: item.url,
            type: item.format,
            label: item.label || '',
            name: item.name,
            isDefault: i === defaultIndex
          }
        })
      })
    }
  })
}

const container = ref<HTMLDivElement | null>(null)
const side = ref<HTMLDivElement | null>(null)
const danmaku = ref<mfunsPlayer['danmaku']['dan']>([])

const typeToNumber = (type: DanmakuPosition) => {
  const types = {
    right: 1,
    top: 5,
    bottom: 4,
    left: 6
  }
  return types[type]
}

onMounted(async () => {
  nextTick(() => {
    try {
      refreshStatus()
    } catch (e) {
      console.error(e)
    }
  })
  window.addEventListener('resize', resizeVideo)
})

onUnmounted(() => {
  window.removeEventListener('resize', resizeVideo)
})

const { data: userInfo } = useAuth()

let player: mfunsPlayer | null = null
watch(
  () => userInfo.value?.login,
  () => {
    try {
      refreshStatus()
    } catch (e) {
      console.error(e)
    }
  }
)

watch(
  () => props.videoList.length,
  () => {
    try {
      refreshStatus()
    } catch (e) {
      console.error(e)
    }
  }
)

function refreshStatus() {
  console.log('刷新播放器状态', props.videoList)
  if (props.videoList.length === 0) return
  loaded.value = false
  if (container.value) {
    const danmakuConfig = useDanmakuConfig().getConfig()
    getList()
    const videoStore = localforage.createInstance({
      name: 'VideoPositionStore'
    })
    player = new mfunsPlayer({
      uid: userInfo.value?.user?.id,
      container: container.value,
      draggable: true,
      hotkey: true,
      autoPlay: playerConfig.autoPlay,
      autoSwitch: playerConfig.autoSwitch,
      autoSkip: playerConfig.autoSkip,
      smallWindow: playerConfig.smallWindow,
      blackBorder: playerConfig.blackBorder,
      volume: playerConfig.volume,
      currentVideo: currentVideo.value,
      video: list,
      widescreenSwitch: true,
      danmaku: {
        api: `${config.baseUrl}/v1/danmaku/get_normal`,
        bottom: '0',
        showDanmaku: danmakuConfig.show,
        shields: danmakuConfig.shields,
        opacity: danmakuConfig.opacity,
        limitArea: danmakuConfig.limitArea,
        fontScale: danmakuConfig.fontScale,
        speed: danmakuConfig.speed,
        keepOutSubtitle: danmakuConfig.keepOutSubtitle,
        showHighEnergy: true,
        danmakuCatch: true
      },
      mutex: true,
      danmakuListAutoScroll: true
    })

    const style = getComputedStyle(document.body)
    const primary = style.getPropertyValue('--color-primary')
    const danmakuEle = document.getElementById(props.danmakuEleId)
    player.mountDanmakuAuxiliary(danmakuEle)
    player.theme(primary)
    player.on('mounted', () => {
      loaded.value = true
    })
    player.on('toLogin', () => {
      useEmitter().emit('requestLogin')
    })
    player.on('webfullscreen', () => {
      fullscreen.value = true
    })
    player.on('webfullscreen_cancel', () => {
      fullscreen.value = false
    })
    player.on('danmaku_send', (danmu) => {
      const dan = danmu as unknown as Danmaku
      const newDan: newDan = {
        video_id: +props.videoId,
        part: player!.currentVideo + 1,
        time: dan.time,
        content: dan.text,
        color: dan.color,
        size: dan.size,
        type: typeToNumber(dan.type)
      }
      requestSendDanmaku(newDan)
    })
    player.on('widescreen', () => {
      widescreen.value = true
      nextTick(() => {
        resizeVideo()
      })
    })
    player.on('widescreen_cancel', () => {
      widescreen.value = false
      nextTick(() => {
        resizeVideo()
      })
    })
    player.on('danmaku_load_end', (dan) => {
      danmaku.value = dan
    })
    player.on('resolution_end', async (v) => {
      const res = player!.resolution.name as VideoResolution
      setResolution(res)
    })
    player.on('setPlayer', (v) => {
      if (v.key === 'volume') {
        usePlayerConfig().setVolume(v.value)
      } else if (v.key === 'muted') {
        usePlayerConfig().setVolume(0)
      } else if (v.key === 'darkMode') {
        lightoff.value = v.value
      } else {
        usePlayerConfig().updateConfig(v.key, v.value)
      }
    })
    player.on('setDanmaku', (v) => {
      useDanmakuConfig().updateConfig(v.key, v.value)
    })
    player.on('update_video_position', async (v) => {
      const key = `p:${props.videoId}:${player!.currentVideo}`
      const time = new Date().getTime()
      const position = v.position
      await videoStore.setItem(key, {
        position,
        time
      })
    })
    player.on('danmaku_report', (dan) => {
      console.log(dan)
    })
    player.on('loadedmetadata', async () => {
      autoSkip()
    })
    player.on('switchVideo_start', (index) => {
      currentVideo.value = index
    })
    const autoSkip = async () => {
      const key = `p:${props.videoId}:${currentVideo.value}`
      const pointer = await videoStore.getItem<{
        position: number
        time: number
      }>(key)
      if (!pointer) return
      if (playerConfig.autoSkip) {
        player?.skip(
          '已为您自动跳转至',
          pointer.position,
          playerConfig.autoSkip
        )
      } else {
        player?.skip(
          '是否跳转至上次观看位置',
          pointer.position,
          playerConfig.autoSkip
        )
      }
    }
  }
}

function resizeVideo() {
  const blackBorder = usePlayerConfig().getConfig().blackBorder
  player?.template.buildVideo(blackBorder)
}

function switchVideo(index: number) {
  currentVideo.value = index
  player?.switchVideo(index)
}

defineExpose({
  switchVideo
})
</script>

<template>
  <div class="m-video-player" :class="{ mobile: screen.sm }">
    <div
      ref="container"
      class="m-video-player__main"
      :style="{
        paddingBottom: loaded ? undefined : paddingBottom
      }"
    ></div>
    <div v-if="!loaded" class="m-video-player__placeholder"></div>
  </div>
</template>

<style lang="scss" scoped>
.m-video-player {
  display: flex;
  position: relative;
  border-radius: 8px;
  background-color: black;
  overflow: hidden;
  &__main {
    width: 100%;
  }
  &__placeholder {
    height: 40px;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 100;
    background-color: #fff;
    border: 1px solid #e6e6e6;
    border-top: 0;
  }
  :deep(.mfunsPlayer-footBar) {
    border: none;
    border-radius: 0;
    .input_box {
      background-color: #fff;
    }
  }
  :deep(.mfunsPlayer-miniPlayer) {
    box-shadow: 0px 4px 8px 3px rgb(0 0 0 / 10%);
    border-radius: 5px;
    background-color: unset;
    .content {
      border-radius: 5px;
      .mfunsPlayer-video-mask {
        border-radius: 7px;
      }
    }
  }
}
</style>

<style lang="scss">
.m-video-player.mobile {
  .mfunsPlayer-controller {
    &-resolution,
    &-repeat,
    &-speed,
    &-volume,
    &-settings,
    &-pip,
    &-widescreen,
    &-webfull {
      display: none;
    }
  }
  .m-video-player__main {
    margin: 0;
  }
  .mfunsPlayer-video-danmaku-details {
    display: none;
  }
}
</style>
